/**
 * Frontend client for the server-backed auth endpoints.
 *
 * Endpoints (mounted via Firebase Hosting rewrite at the same origin so the
 * `mochia_rt` HttpOnly cookie is first-party — see Decision 3):
 *   - POST /api/auth/exchange — code + verifier → access_token + Set-Cookie
 *   - POST /api/auth/refresh  — cookie         → access_token
 *   - POST /api/auth/logout   — cookie         → revoke + clear cookie
 *
 * All requests carry an `X-Firebase-AppCheck` header (App Check enforces
 * "request originated from our PWA" at the function — Decision 7).
 *
 * Error model:
 *   - 200 with valid body → resolved value
 *   - 401 with body.error in known set → throw SessionExpired(reason)
 *   - 400 → throw BadRequest(reason)
 *   - 502 / network / non-JSON → throw UpstreamUnavailable
 *
 * Sharp edge: postLogout NEVER throws — logout is idempotent client-side
 * (the caller always wipes local state regardless of server response).
 */

export type SessionExpiredReason =
  | 'no_session'
  | 'invalid_session'
  | 'grant_revoked'
  | 'app_check_failed';

export type BadRequestReason = 'invalid_code' | 'bad_request';

export class SessionExpired extends Error {
  readonly reason: SessionExpiredReason;
  constructor(reason: SessionExpiredReason) {
    super(`session expired: ${reason}`);
    this.name = 'SessionExpired';
    this.reason = reason;
  }
}

export class UpstreamUnavailable extends Error {
  constructor(message = 'upstream unavailable') {
    super(message);
    this.name = 'UpstreamUnavailable';
  }
}

export class BadRequest extends Error {
  readonly reason: BadRequestReason;
  constructor(reason: BadRequestReason) {
    super(`bad request: ${reason}`);
    this.name = 'BadRequest';
    this.reason = reason;
  }
}

export interface AccessTokenResponse {
  access_token: string;
  expires_at: number;
}

export interface ExchangeResponse extends AccessTokenResponse {
  email: string;
}

interface ServerAuthDeps {
  fetch: typeof fetch;
  getAppCheckToken: () => Promise<string | null>;
  /**
   * Resolves once Firebase App Check initialization has settled (success or
   * graceful fallback). `authedFetch` awaits this BEFORE issuing any request
   * so the first cold-load `postRefresh` / `postExchange` after a PWA boot
   * does not race ahead of init and receive a spurious 401 app_check_failed.
   *
   * Default: an already-resolved promise so tests and non-production embeds
   * that don't wire App Check still get the existing fire-immediately
   * behavior. Production wires the live readiness promise via
   * `configureServerAuth({ appCheckReady })` from main.tsx.
   *
   * Contract: this promise MUST NEVER reject. Init failure resolves the
   * promise so authedFetch can proceed with a null App Check token; the
   * server's structured 401 logging covers diagnostics from there.
   */
  appCheckReady: Promise<void>;
}

// Default deps — production wires App Check via configure(); tests override.
let deps: ServerAuthDeps = {
  fetch: (...args) => globalThis.fetch(...args),
  getAppCheckToken: async () => null,
  appCheckReady: Promise.resolve(),
};

export function configureServerAuth(input: Partial<ServerAuthDeps>): void {
  deps = { ...deps, ...input };
}

/** Test-only: partial-merge override. Unspecified keys preserve current deps. */
export function configureServerAuthForTests(input: Partial<ServerAuthDeps>): void {
  deps = {
    fetch: input.fetch ?? deps.fetch,
    getAppCheckToken: input.getAppCheckToken ?? deps.getAppCheckToken,
    appCheckReady: input.appCheckReady ?? deps.appCheckReady,
  };
}

async function authedFetch(path: string, init: RequestInit): Promise<Response> {
  // Block until App Check init has settled — see ServerAuthDeps.appCheckReady.
  await deps.appCheckReady;
  const appCheck = await deps.getAppCheckToken();
  const headers = new Headers(init.headers);
  if (appCheck) headers.set('X-Firebase-AppCheck', appCheck);
  return deps.fetch(path, {
    ...init,
    headers,
    credentials: 'include',
  });
}

async function readJson(res: Response): Promise<unknown> {
  const ct = res.headers.get('content-type') ?? '';
  if (!ct.includes('application/json')) {
    throw new UpstreamUnavailable(`non-JSON response (${res.status})`);
  }
  try {
    return await res.json();
  } catch {
    throw new UpstreamUnavailable('invalid JSON');
  }
}

function classify401(error: unknown): SessionExpired {
  if (
    error === 'no_session' ||
    error === 'invalid_session' ||
    error === 'grant_revoked' ||
    error === 'app_check_failed'
  ) {
    return new SessionExpired(error);
  }
  // Unknown 401 reason → treat as invalid_session (server already cleared cookie)
  return new SessionExpired('invalid_session');
}

function isAccessTokenResponse(o: unknown): o is AccessTokenResponse {
  if (typeof o !== 'object' || o === null) return false;
  const r = o as Record<string, unknown>;
  return typeof r.access_token === 'string' && typeof r.expires_at === 'number';
}

function isExchangeResponse(o: unknown): o is ExchangeResponse {
  return isAccessTokenResponse(o) && typeof (o as ExchangeResponse).email === 'string';
}

export async function postExchange(input: {
  code: string;
  codeVerifier: string;
}): Promise<ExchangeResponse> {
  let res: Response;
  try {
    res = await authedFetch('/api/auth/exchange', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code: input.code, code_verifier: input.codeVerifier }),
    });
  } catch {
    throw new UpstreamUnavailable('network error');
  }
  if (res.status === 200) {
    const body = await readJson(res);
    if (!isExchangeResponse(body)) throw new UpstreamUnavailable('malformed exchange response');
    return body;
  }
  if (res.status === 400) {
    const body = (await readJson(res).catch(() => ({}))) as { error?: unknown };
    const reason = body.error === 'invalid_code' ? 'invalid_code' : 'bad_request';
    throw new BadRequest(reason);
  }
  if (res.status === 401) {
    const body = (await readJson(res).catch(() => ({}))) as { error?: unknown };
    throw classify401(body.error);
  }
  // 502 / 5xx / anything else → upstream unavailable
  throw new UpstreamUnavailable(`status ${res.status}`);
}

export async function postRefresh(): Promise<AccessTokenResponse> {
  let res: Response;
  try {
    res = await authedFetch('/api/auth/refresh', { method: 'POST' });
  } catch {
    throw new UpstreamUnavailable('network error');
  }
  if (res.status === 200) {
    const body = await readJson(res);
    if (!isAccessTokenResponse(body)) throw new UpstreamUnavailable('malformed refresh response');
    return body;
  }
  if (res.status === 401) {
    const body = (await readJson(res).catch(() => ({}))) as { error?: unknown };
    throw classify401(body.error);
  }
  throw new UpstreamUnavailable(`status ${res.status}`);
}

/**
 * Fire-and-forget logout. Always resolves — local cleanup must never depend
 * on server reachability. The caller is responsible for clearing in-memory
 * token and localStorage hints regardless of this promise's outcome.
 */
export async function postLogout(): Promise<void> {
  try {
    await authedFetch('/api/auth/logout', { method: 'POST' });
  } catch {
    /* swallow — logout is idempotent client-side */
  }
}
